/**
 * Ceiling-tile microphone (BMA360D) coverage plot.
 *
 * Ceiling-tile mics have a much larger pickup radius than ceiling mics (TCM-X),
 * so the shared `micDistance` heatmap floods a normal room with a flat green
 * slab and the coverage never reads as a circle. Instead we draw an explicit
 * coverage circle per unit at the WDT-75 radius (`microphones.micMaxRadius`),
 * using the same green/opacity ramp as the TCM-X `micDistance` gradient so the
 * two mic types look consistent (PIG-483).
 *
 * Rendering matches the heatmap's union semantics: a single masked rect fills
 * the union of all units' circles, so overlapping coverage composites like the
 * heatmap (no additive darkening) while keeping a soft edge.
 */
import React from "react";
import { useSelector } from "react-redux";
import { getDrawingBox } from "reduxModules/ducks/ui";
import { RootState } from "types/redux/state";
import { micInCategory } from "types/models/microphone";

// micDistance gradient: hsl(124, 1, 0.5) -> rgb(0, 255, 17), 0.8 opaque to the
// target radius then linearly fading to 0 at 1.4x target (fade = 0.5).
const COVERAGE_COLOR = "rgb(0, 255, 17)";
const COVERAGE_OPACITY = 0.8;
const FADE_FACTOR = 1.4; // outer radius = micMaxRadius * 1.4
const CORE_FRACTION = 1 / FADE_FACTOR; // solid out to this fraction of the circle

const MuiCeilingTileCoverage: React.FC = () => {
  const { bbox, scale, zoom } = useSelector(getDrawingBox);
  const areas = useSelector((state: RootState) => state.container.areas);
  const currentView = useSelector((state: RootState) => state.ui.currentView);
  const currentDevice = useSelector((state: RootState) => state.ui.currentDevice);
  const showMap = useSelector((state: RootState) => state.ui.showMap);

  // Only render in the microphone coverage view (mirrors the heatmap gating).
  const inMicCoverageView =
    currentDevice === "microphones" &&
    showMap === "micDistance" &&
    ["acoustics", "microphones", "speakers"].includes(currentView ?? "");
  if (!inMicCoverageView) return null;
  if (!bbox || !scale) return null;

  const coverageAreas = Object.values(areas).filter((area: any) =>
    area?.microphones?.layout?.length &&
    micInCategory(area.microphones.model, "CEILING_TILE") &&
    area.microphones.micMaxRadius > 0
  );
  if (coverageAreas.length === 0) return null;

  return (
    <g id="ceiling-tile-coverage" style={{ pointerEvents: "none" }}>
      <defs>
        {/* White = revealed by the mask; soft edge beyond the core radius. */}
        <radialGradient id="ceiling-tile-coverage-grad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fff" stopOpacity="1" />
          <stop offset={`${CORE_FRACTION * 100}%`} stopColor="#fff" stopOpacity="1" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
      </defs>

      {coverageAreas.map((area: any) => {
        const { location, dimensions, microphones } = area;
        const left = location?.left || 0;
        const top = location?.top || 0;
        const originX = bbox.x0 + left * scale * (!zoom ? 1 : 0);
        const originY = bbox.y0 + top * scale * (!zoom ? 1 : 0);
        const outerR = microphones.micMaxRadius * FADE_FACTOR * scale;

        const maskId = `ceiling-tile-coverage-mask-${area.id}`;
        // Confine the fill to this area's floorplan rectangle.
        const rectX = originX;
        const rectY = originY;
        const rectW = (dimensions?.length || 0) * scale;
        const rectH = (dimensions?.width || 0) * scale;

        return (
          <g key={area.id}>
            <mask id={maskId}>
              {microphones.layout.map((mic: any, i: number) => (
                <circle
                  key={i}
                  cx={originX + mic.location.x * scale}
                  cy={originY + mic.location.y * scale}
                  r={outerR}
                  fill="url(#ceiling-tile-coverage-grad)"
                />
              ))}
            </mask>
            <rect
              x={rectX}
              y={rectY}
              width={rectW}
              height={rectH}
              fill={COVERAGE_COLOR}
              fillOpacity={COVERAGE_OPACITY}
              mask={`url(#${maskId})`}
            />
          </g>
        );
      })}
    </g>
  );
};

export default MuiCeilingTileCoverage;
